/**
 * Auth Form Validation Schemas
 * Zod schemas for client-side form validation
 */

import { z, validateSaudiPhone } from '@spcc/core'

// ============================================================================
// Saudi Regions
// ============================================================================

export const SAUDI_REGIONS = [
  'riyadh',
  'makkah',
  'madinah',
  'qassim',
  'eastern',
  'asir',
  'tabuk',
  'hail',
  'northern_borders',
  'jazan',
  'najran',
  'bahah',
  'jawf',
] as const

export type SaudiRegion = typeof SAUDI_REGIONS[number]

// ============================================================================
// Login Form Schema
// ============================================================================

export const loginFormSchema = z.object({
  identifier: z
    .string()
    .min(1, 'auth.validation.identifierRequired')
    .email('auth.validation.invalidEmail'),
  password: z
    .string()
    .min(1, 'auth.validation.passwordRequired')
    .min(6, 'auth.validation.passwordMinLength'),
  rememberMe: z.boolean().optional(),
})

export type LoginFormData = z.infer<typeof loginFormSchema>

// ============================================================================
// Register Form Schema — Step 1 (Account Info)
// ============================================================================

export const registerStep1Schema = z.object({
  fullName: z
    .string()
    .min(1, 'auth.validation.fullNameRequired')
    .max(100, 'auth.validation.fullNameMaxLength'),
  companyName: z
    .string()
    .min(1, 'auth.validation.companyNameRequired')
    .max(100, 'auth.validation.companyNameMaxLength'),
  email: z
    .string()
    .min(1, 'auth.validation.emailRequired')
    .email('auth.validation.invalidEmail'),
  mobilePhone: z
    .string()
    .min(1, 'auth.validation.mobilePhoneRequired')
    .refine((v) => validateSaudiPhone(v).valid, 'validation.phoneSaudiFormat'),
  telephone: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((v) => !v || v.trim() === '' || validateSaudiPhone(v).valid, 'validation.phoneSaudiFormat'),
  password: z
    .string()
    .min(1, 'auth.validation.passwordRequired')
    .min(6, 'auth.validation.passwordMinLength')
    .max(100, 'auth.validation.passwordMaxLength'),
  confirmPassword: z
    .string()
    .min(1, 'auth.validation.confirmPasswordRequired'),
  agreeToTerms: z
    .boolean()
    .refine(value => value === true, {
      message: 'auth.validation.agreeToTermsRequired',
    }),
}).refine(data => data.password === data.confirmPassword, {
  message: 'auth.validation.passwordsMismatch',
  path: ['confirmPassword'],
})

export type RegisterStep1Data = z.infer<typeof registerStep1Schema>

// ============================================================================
// Register Form Schema — Step 2 (Company Details & Documents)
// ============================================================================

export const registerStep2Schema = z.object({
  vatNumber: z
    .string()
    .min(1, 'auth.validation.vatNumberRequired')
    .max(50, 'auth.validation.vatNumberMaxLength'),
  addressLine1: z
    .string()
    .min(1, 'auth.validation.addressLine1Required')
    .max(255, 'auth.validation.addressLine1MaxLength'),
  houseNumber: z
    .string()
    .regex(/^\d{1,4}$/, 'auth.validation.houseNumberInvalid')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .min(1, 'auth.validation.cityRequired')
    .max(100, 'auth.validation.cityMaxLength'),
  region: z
    .enum(SAUDI_REGIONS, { error: 'auth.validation.regionRequired' }),
  postalCode: z
    .string()
    .min(1, 'auth.validation.postalCodeRequired')
    .max(20, 'auth.validation.postalCodeMaxLength'),
})

export type RegisterStep2Data = z.infer<typeof registerStep2Schema>

// ============================================================================
// Full Register Form Schema (combined, for final validation before submit)
// ============================================================================

export const registerFormSchema = registerStep1Schema.and(registerStep2Schema)

export type RegisterFormData = z.infer<typeof registerFormSchema>

// ============================================================================
// Forgot Password Form Schema
// ============================================================================

export const forgotPasswordFormSchema = z.object({
  email: z
    .string()
    .min(1, 'auth.validation.emailRequired')
    .email('auth.validation.invalidEmail'),
})

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordFormSchema>

// ============================================================================
// Reset Password Form Schema
// ============================================================================

export const resetPasswordFormSchema = z.object({
  code: z
    .string()
    .min(1, 'auth.validation.codeRequired')
    .length(4, 'auth.validation.codeLength'),
  password: z
    .string()
    .min(1, 'auth.validation.passwordRequired')
    .min(6, 'auth.validation.passwordMinLength')
    .max(100, 'auth.validation.passwordMaxLength'),
  confirmPassword: z
    .string()
    .min(1, 'auth.validation.confirmPasswordRequired'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'auth.validation.passwordsMismatch',
  path: ['confirmPassword'],
})

export type ResetPasswordFormData = z.infer<typeof resetPasswordFormSchema>

// ============================================================================
// Field Names Constants
// ============================================================================

export const AUTH_FORM_FIELDS = {
  LOGIN: {
    IDENTIFIER: 'identifier',
    PASSWORD: 'password',
    REMEMBER_ME: 'rememberMe',
  },
  REGISTER: {
    // Step 1
    EMAIL: 'email',
    PASSWORD: 'password',
    CONFIRM_PASSWORD: 'confirmPassword',
    FULL_NAME: 'fullName',
    COMPANY_NAME: 'companyName',
    MOBILE_PHONE: 'mobilePhone',
    TELEPHONE: 'telephone',
    AGREE_TO_TERMS: 'agreeToTerms',
    // Step 2
    VAT_NUMBER: 'vatNumber',
    ADDRESS_LINE_1: 'addressLine1',
    HOUSE_NUMBER: 'houseNumber',
    CITY: 'city',
    REGION: 'region',
    POSTAL_CODE: 'postalCode',
  },
  FORGOT_PASSWORD: {
    EMAIL: 'email',
  },
  RESET_PASSWORD: {
    CODE: 'code',
    PASSWORD: 'password',
    CONFIRM_PASSWORD: 'confirmPassword',
  },
} as const

// ============================================================================
// Translation Keys
// ============================================================================

export const AUTH_FORM_T_KEYS = {
  FIELDS: {
    EMAIL: 'auth.fields.email',
    PASSWORD: 'auth.fields.password',
    CONFIRM_PASSWORD: 'auth.fields.confirmPassword',
    FULL_NAME: 'auth.fields.fullName',
    COMPANY_NAME: 'auth.fields.companyName',
    PHONE: 'auth.fields.phone',
    MOBILE_PHONE: 'auth.fields.mobilePhone',
    TELEPHONE: 'auth.fields.telephone',
    VAT_NUMBER: 'auth.fields.vatNumber',
    ADDRESS_LINE_1: 'auth.fields.addressLine1',
    HOUSE_NUMBER: 'auth.fields.houseNumber',
    CITY: 'auth.fields.city',
    REGION: 'auth.fields.region',
    POSTAL_CODE: 'auth.fields.postalCode',
    ATTACHMENTS: 'auth.fields.attachments',
    RESET_CODE: 'auth.fields.resetCode',
    REMEMBER_ME: 'auth.fields.rememberMe',
    AGREE_TO_TERMS: 'auth.fields.agreeToTerms',
  },
  PLACEHOLDERS: {
    EMAIL: 'auth.placeholders.email',
    PASSWORD: 'auth.placeholders.password',
    CONFIRM_PASSWORD: 'auth.placeholders.confirmPassword',
    FULL_NAME: 'auth.placeholders.fullName',
    COMPANY_NAME: 'auth.placeholders.companyName',
    PHONE: 'auth.placeholders.phone',
    MOBILE_PHONE: 'auth.placeholders.mobilePhone',
    TELEPHONE: 'auth.placeholders.telephone',
    VAT_NUMBER: 'auth.placeholders.vatNumber',
    ADDRESS_LINE_1: 'auth.placeholders.addressLine1',
    HOUSE_NUMBER: 'auth.placeholders.houseNumber',
    CITY: 'auth.placeholders.city',
    POSTAL_CODE: 'auth.placeholders.postalCode',
    RESET_CODE: 'auth.placeholders.resetCode',
  },
  BUTTONS: {
    SIGN_IN: 'auth.buttons.signIn',
    SIGN_UP: 'auth.buttons.signUp',
    FORGOT_PASSWORD: 'auth.buttons.forgotPassword',
    RESET_PASSWORD: 'auth.buttons.resetPassword',
    SEND_RESET: 'auth.buttons.sendReset',
    BACK_TO_LOGIN: 'auth.buttons.backToLogin',
    NEXT: 'auth.buttons.next',
    BACK: 'auth.buttons.back',
  },
  VALIDATION: {
    IDENTIFIER_REQUIRED: 'auth.validation.identifierRequired',
    EMAIL_REQUIRED: 'auth.validation.emailRequired',
    INVALID_EMAIL: 'auth.validation.invalidEmail',
    PASSWORD_REQUIRED: 'auth.validation.passwordRequired',
    PASSWORD_MIN_LENGTH: 'auth.validation.passwordMinLength',
    PASSWORD_MAX_LENGTH: 'auth.validation.passwordMaxLength',
    CONFIRM_PASSWORD_REQUIRED: 'auth.validation.confirmPasswordRequired',
    PASSWORDS_MISMATCH: 'auth.validation.passwordsMismatch',
    FULL_NAME_REQUIRED: 'auth.validation.fullNameRequired',
    FULL_NAME_MAX_LENGTH: 'auth.validation.fullNameMaxLength',
    COMPANY_NAME_REQUIRED: 'auth.validation.companyNameRequired',
    COMPANY_NAME_MAX_LENGTH: 'auth.validation.companyNameMaxLength',
    PHONE_REQUIRED: 'auth.validation.phoneRequired',
    PHONE_INVALID: 'auth.validation.phoneInvalid',
    MOBILE_PHONE_REQUIRED: 'auth.validation.mobilePhoneRequired',
    MOBILE_PHONE_INVALID: 'auth.validation.mobilePhoneInvalid',
    TELEPHONE_INVALID: 'auth.validation.telephoneInvalid',
    VAT_NUMBER_REQUIRED: 'auth.validation.vatNumberRequired',
    ADDRESS_LINE_1_REQUIRED: 'auth.validation.addressLine1Required',
    CITY_REQUIRED: 'auth.validation.cityRequired',
    REGION_REQUIRED: 'auth.validation.regionRequired',
    POSTAL_CODE_REQUIRED: 'auth.validation.postalCodeRequired',
    HOUSE_NUMBER_INVALID: 'auth.validation.houseNumberInvalid',
    ATTACHMENTS_REQUIRED: 'auth.validation.attachmentsRequired',
    CODE_REQUIRED: 'auth.validation.codeRequired',
    CODE_LENGTH: 'auth.validation.codeLength',
    AGREE_TO_TERMS_REQUIRED: 'auth.validation.agreeToTermsRequired',
  },
} as const



